/*
 * NEXT, AS THE CARD DRAWS IT: the arrangement record/nextModel.mjs leaves to the card.
 *
 * nextModel decides the lines, their words, their controls and which one is the
 * yellow. What is left is drawing and the card's own memory, and each piece of it
 * was a choice worth pinning:
 *
 *   splitControls     which controls sit beside a line's title and which on the row
 *                     under it (frame 1g: Re-engage beside, Move the wake-up and Stop
 *                     reminders below), and what the phone leaves to its bottom bar
 *   pendingGroups     the waiting changes as verdicts: rows that share one piece of
 *                     evidence are ONE decision (the ghost sweep writes a company's
 *                     exit and its contact's stamp separately), so Review approves or
 *                     dismisses them together, as the Approvals page does
 *   bulkFailure       the server's refusal of a verdict, in a sentence beside it
 *   wakeChoices       Move the wake-up's chips, the Remove confirm's own grammar
 *   receipts          the ✓ lines kept in sessionStorage, so a receipt survives the
 *                     panel being closed and opened again, not just the reload after
 *                     the send (the shell keeps those)
 *
 * Pure, so test/record-next-list.test.js pins it without a browser.
 */
import { sinceWords } from "./nextModel.mjs";
import { REMIND_MONTHS } from "./endings.mjs";
import { ymd, addCalendar, dayOf } from "./statusConfirm.mjs";
import { toDate, longDate } from "./format.mjs";

/* ------------------------------------------------------------------ controls */

/**
 * A line's controls, split into the ones beside its title and the ones on the row
 * under it. Two fit beside the title (owed: Send, Mark sent). A line with more
 * than two keeps its main control beside the title and puts the rest under it:
 * the wake-up's Move the wake-up and Stop reminders change the reminder, not the
 * company, and frame 1g draws them on their own row. On the phone the yellow
 * control is the bottom bar's (PhoneBar), so the card leaves it out.
 */
export function splitControls(line, { phone = false, yellowHere = false } = {}) {
  const all = (line && line.controls) || [];
  const head = phone && yellowHere ? [] : all.slice(0, 1);
  if (all.length > 2) return { inline: head, below: all.slice(1) };
  return { inline: phone && yellowHere ? all.slice(1) : all, below: [] };
}

/** Is this control THE yellow button? Only the top line's first, never on the phone. */
export function isYellow(next, lineIndex, control, { phone = false } = {}) {
  if (phone || !next || !next.yellow || !control) return false;
  if (next.yellow.line !== lineIndex) return false;
  const line = next.lines && next.lines[lineIndex];
  return !!line && line.controls && line.controls[0] === control && control.id === next.yellow.control;
}

/* ------------------------------------------------------------------ approvals */

/*
 * Stored evidence lines carry the watcher's spaced em or en dash ('PandaDoc "MSA"
 * viewed, dash, matched by …'). The record's copy never shows one, so a spaced dash
 * reads as the house's middle dot here; the words themselves are kept. Written as
 * escapes so this file carries no dash of its own.
 */
export function evidenceWords(text) {
  if (!text) return null;
  return String(text).replace(/\s+[\u2014\u2013]\s+/g, " · ").trim() || null;
}

/**
 * The waiting changes, grouped into verdicts: one group per evidence_ref (one email,
 * one call, one sweep run), else one per row. Each group is approved or dismissed as
 * a whole, through POST /pending-changes/bulk, which answers per row.
 */
export function pendingGroups(items) {
  const groups = new Map();
  for (const item of items || []) {
    if (!item) continue;
    const p = item.proposal || {};
    const key = p.evidence_ref ? `ref:${p.evidence_ref}` : `id:${item.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return [...groups.entries()].map(([key, rows]) => {
    const first = rows[0];
    const ref = (first.proposal && first.proposal.evidence_ref) || null;
    return {
      key,
      ids: rows.map((r) => r.id),
      lines: rows.map((r) => r.text),
      evidence: evidenceWords(first.evidence || (first.proposal && first.proposal.evidence)),
      // Only a real address is a link: a sweep's ref names a run, not a page.
      source_url: ref && /^https?:\/\//.test(ref) ? ref : null,
    };
  });
}

const REFUSALS = {
  STAGE_ON_NON_PROSPECT: "This company is out of the pipeline now, so the stage it proposes no longer applies. Dismiss it instead.",
  PIPELINE_STAGE_REQUIRED: "Putting a company back in the pipeline needs a stage, and this change carries none.",
};

/** The bulk route's per-row answers, as the one sentence to show by the button, or null. */
export function bulkFailure(results) {
  for (const r of results || []) {
    if (!r) continue;
    if (r.status === "error") return REFUSALS[r.code] || r.error || "It couldn’t be applied.";
    if (r.status === "already_resolved") return "Already settled elsewhere.";
    if (r.status === "not_found") return "It no longer exists.";
  }
  return null;
}

/* ------------------------------------------------------------------ the wake-up */

/** Move the wake-up: the Remove confirm's "Remind me" months, then a date. */
export function wakeChoices() {
  return [
    ...REMIND_MONTHS.map((m) => ({ id: `${m}m`, months: m, label: m === REMIND_MONTHS[0] ? `In ${m} months` : `${m} months` })),
    { id: "date", label: "A date…" },
  ];
}

/** The new wake-up day for a choice, "YYYY-MM-DD", or null until one is picked. */
export function wakeDate(choice, { now = new Date(), date = null } = {}) {
  if (!choice) return null;
  if (choice === "date") return date && dayOf(date) && date > ymd(now) ? String(date).slice(0, 10) : null;
  const m = Number(String(choice).replace(/m$/, ""));
  return Number.isFinite(m) && m > 0 ? ymd(addCalendar(now, { months: m })) : null;
}

/** "Wakes Jan 1, 2027. Fieldline stays parked until then." */
export function wakeSentence(day, companyName) {
  const d = dayOf(day);
  if (!d) return null;
  return `Wakes ${longDate(d)}. ${companyName || "It"} stays parked until then, and comes back by itself if they write.`;
}

/* ------------------------------------------------------------------ receipts */

/*
 * How long a ✓ line is worth keeping. The receipt answers "did that go?", which
 * matters for the rest of the sitting, not tomorrow: after an hour the timeline
 * says it better.
 */
export const RECEIPT_TTL_MINUTES = 60;

/** One sessionStorage key per company (the record's people share its Next). */
export function receiptStoreKey({ company = null, contact = null } = {}) {
  if (company && company.id) return `rec-receipts:co:${company.id}`;
  if (contact && contact.id) return `rec-receipts:ct:${contact.id}`;
  return null;
}

// "receipt:{kind}:{contact_id}:{at}", the key nextModel gives a receipt row. The
// instant itself has colons, so only the first three parts are read.
function parseKey(key) {
  const parts = String(key || "").split(":");
  return { kind: parts[1] || null, contact_id: parts[2] || null };
}

// The receipt's words without the "· just now" nextModel put on them, so a
// restored receipt can say how long ago it is NOW.
function baseOf(text) {
  const s = String(text || "");
  const i = s.lastIndexOf(" · ");
  return i > 0 ? s.slice(0, i) : s;
}

const fresh = (r, now) => {
  const t = toDate(r && r.at);
  return !!t && toDate(now).getTime() - t.getTime() <= RECEIPT_TTL_MINUTES * 60000;
};

/**
 * The stored receipts with the card's current ones folded in, newest last, stale
 * ones dropped: what the card writes back to sessionStorage.
 */
export function keepReceipts(stored, rows, now = new Date()) {
  const byKey = new Map();
  for (const r of stored || []) if (r && r.key && fresh(r, now)) byKey.set(r.key, r);
  for (const r of rows || []) {
    if (!r || !r.key || !fresh(r, now)) continue;
    const { kind, contact_id } = parseKey(r.key);
    byKey.set(r.key, { key: r.key, kind: r.kind || kind, contact_id, base: baseOf(r.text), at: r.at, thread_url: r.thread_url || null });
  }
  return [...byKey.values()].sort((a, b) => toDate(a.at) - toDate(b.at));
}

/** Did keepReceipts change anything worth writing? */
export function sameReceipts(a, b) {
  const ka = (a || []).map((r) => r.key).join("|");
  const kb = (b || []).map((r) => r.key).join("|");
  return ka === kb;
}

/**
 * The ✓ rows to draw: the card's own (the shell's, this visit), then any stored
 * from an earlier visit whose line is gone. A stored receipt whose action is back
 * in Next (they wrote again) is not drawn: "Replied to Ella" above "Reply waiting ·
 * Ella" would contradict itself.
 */
export function shownReceipts(stored, next, now = new Date()) {
  const live = (next && next.receipts) || [];
  const liveKeys = new Set(live.map((r) => r.key));
  const lineKeys = new Set(((next && next.lines) || []).map((l) => l.key));
  const restored = (stored || [])
    .filter((r) => r && fresh(r, now) && !liveKeys.has(r.key) && !lineKeys.has(`${r.kind}:${r.contact_id || ""}`))
    .map((r) => ({ key: r.key, kind: r.kind, text: `${r.base} · ${sinceWords(r.at, now)}`, thread_url: r.thread_url || null, at: r.at, restored: true }));
  return [...restored, ...live].sort((a, b) => toDate(a.at) - toDate(b.at));
}
